<div class="container-fluid">

  <!-- Page Heading -->
  <h1 class="h3 mb-2 text-gray-800">
    <i class="fas fa-user"></i>
    {{ memberI18n.allTitleI18n }}
  </h1>
  <p class="mb-4 text-muted">
    The Members Management module allows administrators to oversee and manage all members within the system. You can view
    detailed lists of members, add new members, edit existing member information, and delete members as needed. This module
    provides a comprehensive interface for maintaining an organized and efficient member database, ensuring smooth operations
    and up-to-date records.
  </p>

  <!-- DataTales Example -->
  <div class="card mb-4">
    <div class="card-body">
      <div class="table-responsive">

        <!-- ── Action Buttons ───────────────────────────────────────── -->
        <button href="#add" role="button" data-toggle="modal" class="btn btn-outline-success btn-icon-split">
          <span class="icon text-success"><i class="fas fa-plus"></i></span>
          <span class="text">Create</span>
        </button>

        <button role="button" class="btn btn-outline-warning btn-icon-split" (click)="getAll()">
          <span class="icon text-warning"><i class="fas fa-redo"></i></span>
          <span class="text">Reload</span>
        </button>

        <!-- Import button — opens the import modal -->
        <button role="button" class="btn btn-outline-primary btn-icon-split"
                data-toggle="modal" data-target="#importMembersModal">
          <span class="icon text-primary"><i class="fas fa-file-import"></i></span>
          <span class="text">Import</span>
        </button>

        <!-- ── Summary Cards ───────────────────────────────────────── -->
        <div class="container mt-5">
          <div class="row">
            <div class="col-md-3 mb-4">
              <div class="card text-center border">
                <div class="card-body">
                  <h5 class="card-title text-info">New Arrivals</h5>
                  <p class="card-text h4">
                    <span *ngIf="newArrivals">{{ newArrivals }}</span>
                    <span *ngIf="!newArrivals">Loading...</span>
                  </p>
                </div>
              </div>
            </div>

            <div class="col-md-3 mb-4">
              <div class="card text-center border">
                <div class="card-body">
                  <h5 class="card-title text-danger">Blacklist</h5>
                  <p class="card-text h4">
                    <span *ngIf="blacklist">{{ blacklist }}</span>
                    <span *ngIf="!blacklist">Loading...</span>
                  </p>
                </div>
              </div>
            </div>

            <div class="col-md-3 mb-4">
              <div class="card text-center border">
                <div class="card-body">
                  <h5 class="card-title text-warning">Unverified Users</h5>
                  <p class="card-text h4">
                    <span *ngIf="unverified">{{ unverified }}</span>
                    <span *ngIf="!unverified">Loading...</span>
                  </p>
                </div>
              </div>
            </div>

            <div class="col-md-3 mb-4">
              <div class="card text-center border">
                <div class="card-body">
                  <h5 class="card-title text-secondary">Expired Users</h5>
                  <p class="card-text h4">
                    <span *ngIf="expired">{{ expired }}</span>
                    <span *ngIf="!expired">Loading...</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ── Spinner / List ──────────────────────────────────────── -->
        <div *ngIf="loading; else loaded">
          <div class="d-flex justify-content-center">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Loading...</span>
            </div>
          </div>
        </div>

        <ng-template #loaded>
          <app-member-list
            (editEvent)="edit($event)"
            (viewEvent)="view($event)"
            (deleteEvent)="delete($event)"
            [members]="members$"
            [memberI18n]="memberI18n"
          ></app-member-list>
          <app-member-modal
            [memberI18n]="memberI18n"
            [id]="id"
          ></app-member-modal>
        </ng-template>
      </div>
    </div>
  </div>
</div>

<!-- ══════════════════════════════════════════════════════════════════ -->
<!-- Import Members Modal                                              -->
<!-- ══════════════════════════════════════════════════════════════════ -->
<div class="modal fade" id="importMembersModal" tabindex="-1" role="dialog"
     aria-labelledby="importMembersModalTitle" aria-hidden="true">
  <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
    <div class="modal-content">

      <div class="modal-header">
        <h5 class="modal-title" id="importMembersModalTitle">
          <i class="fas fa-file-import text-primary mr-2"></i> Import Members
        </h5>
        <button type="button" class="close" data-dismiss="modal" aria-label="Close"
                (click)="resetImport()">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>

      <div class="modal-body">

        <!-- Instructions -->
        <div class="alert alert-info d-flex align-items-start" role="alert">
          <i class="fas fa-info-circle mr-2 mt-1"></i>
          <div>
            Upload an <strong>.xlsx</strong> or <strong>.csv</strong> file.<br>
            <strong>Required columns:</strong> <code>surname</code>, <code>firstname</code><br>
            All other columns (admission_number, dob, gender, class, city …) are optional.<br>
            <a href="#" (click)="downloadTemplate($event)" class="alert-link">
              <i class="fas fa-download mr-1"></i>Download template
            </a>
          </div>
        </div>

        <!-- File picker -->
        <div class="form-group">
          <label for="importFile"><strong>Select file</strong></label>
          <div class="custom-file">
            <input type="file" class="custom-file-input" id="importFile"
                   accept=".xlsx,.xls,.csv"
                   (change)="onFileSelected($event)">
            <label class="custom-file-label" for="importFile">
              {{ selectedFileName || 'Choose file…' }}
            </label>
          </div>
        </div>

        <!-- Upload progress -->
        <div *ngIf="importing">
          <div class="d-flex align-items-center mb-2">
            <div class="spinner-border spinner-border-sm text-primary mr-2" role="status"></div>
            <span>Importing, please wait…</span>
          </div>
          <div class="progress">
            <div class="progress-bar progress-bar-striped progress-bar-animated"
                 role="progressbar" style="width: 100%"></div>
          </div>
        </div>

        <!-- Result summary -->
        <div *ngIf="importResult">
          <div class="alert" [ngClass]="importResult.errors?.length ? 'alert-warning' : 'alert-success'">
            <strong>Import complete.</strong>
            {{ importResult.imported }} record(s) imported,
            {{ importResult.skipped }} skipped.
          </div>

          <!-- Per-row errors -->
          <div *ngIf="importResult.errors?.length" class="mt-2">
            <h6 class="text-danger"><i class="fas fa-exclamation-triangle mr-1"></i>Row errors</h6>
            <ul class="list-group list-group-flush" style="max-height:200px; overflow-y:auto;">
              <li *ngFor="let err of importResult.errors"
                  class="list-group-item list-group-item-danger py-1 px-2 small">
                {{ err }}
              </li>
            </ul>
          </div>
        </div>

      </div><!-- /modal-body -->

      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-dismiss="modal"
                (click)="resetImport()">
          Close
        </button>
        <button type="button" class="btn btn-primary"
                [disabled]="!selectedFile || importing"
                (click)="submitImport()">
          <i class="fas fa-upload mr-1"></i>
          {{ importing ? 'Importing…' : 'Import' }}
        </button>
      </div>

    </div>
  </div>
</div>